import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Lightbox } from 'ngx-lightbox';
import AssetHelper from 'src/app/helpers/asset-helper';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import { BoardTypeChoices } from 'src/app/model/product/board/board-part';
import { ProductTypes } from 'src/app/model/product/product-type';
import { SUB_PROJECT_TYPES } from 'src/app/model/project/sub-project';
import { ModalService } from 'src/app/services/modal.service';
import IPBoardPart from '../../../../model/product/ip-board/ip-board-part';
import { AuthService } from '../../../../services/auth.service';
import { SnackService } from '../../../../services/snack.service';
import { ClientIPBoardAndPlacementHelper } from '../../../../shared/project-components/client-project-form/base-project/client-ip-board-form/helper/client-ip-board-and-placement-helper';
import { AtelierControllerService } from '../../service/atelier-controller.service';
import { AtelierTourItemHelper } from '../atelier-tour-item-helper';
import { AtelierDetailProduct } from '../model/atelier-detail-product';
import { AtelierDetailTourItem } from '../model/atelier-detail-tour-item';
import { AtelierUpdateCheckedIdentifier } from '../model/atelier-update-checked-identifier';
import { AtelierUpdateCheckedResponse } from '../model/atelier-update-checked-response';
import { AtelierUpdateStockResponse } from '../model/atelier-update-stock-response';
import {
  AtelierConfigurationInfo,
  AtelierConfigurationInfoKeys,
  AtelierItemDetailHelper,
} from './helper/atelier-item-detail-helper';

@Component({
  selector: 'atelier-item-detail',
  templateUrl: './atelier-item-detail.component.html',
  styleUrls: ['./atelier-item-detail.component.scss'],
})
export class AtelierItemDetailComponent implements OnInit {
  @Input()
  item: AtelierDetailTourItem;
  @Input()
  reviewingUserId: number;
  @Input()
  atelierUserId: number;

  @Output()
  checkUpdate = new EventEmitter<AtelierUpdateCheckedResponse>();
  @Output()
  stockUpdate = new EventEmitter<AtelierUpdateStockResponse>();

  BOARD_TYPE_CHOICES = BoardTypeChoices;
  SUB_PROJECT_TYPES = SUB_PROJECT_TYPES;

  reviewDisabled: boolean = true;
  finishDisabled: boolean = true;

  boardSummary: string;
  boardSummaryRight: string;
  placementTypePreviewUrl: string;
  title: string;

  leftDefaultTitle: boolean;
  leftTitleFromStock: boolean;
  leftTitleFromPrintwork: boolean;
  leftDefaultPhoneNumber: boolean;
  leftPhoneNumberFromStock: boolean;
  leftPhoneNumberFromPrintwork: boolean;

  rightDefaultTitle: boolean;
  rightTitleFromStock: boolean;
  rightTitleFromPrintwork: boolean;
  rightDefaultPhoneNumber: boolean;
  rightPhoneNumberFromStock: boolean;
  rightPhoneNumberFromPrintwork: boolean;

  printRegisteredDate: Date;
  printRegisteredUser: string;
  textRegisteredDate: Date;
  textRegisteredUser: string;
  extraBoardTextRegisteredDate: Date;
  extraBoardTextRegisteredUser: string;

  textColors: string;
  textColorsRight: string;
  textCheckboxLabel: string = 'Tekst';

  amount: number = 0;

  constructor(
    private atelierService: AtelierControllerService,
    private snackService: SnackService,
    private atelierTourItemHelper: AtelierTourItemHelper,
    private authService: AuthService,
    private _lightbox: Lightbox,
    private modalService: ModalService
  ) {}

  ngOnInit() {
    this.calculateBoardSummary();
    this.calculatePlacementTypePreviewUrl();
    this.reviewDisabled =
      this.authService.getAuthenticatedUser().id !== this.reviewingUserId;
    this.finishDisabled =
      this.authService.getAuthenticatedUser().id !== this.atelierUserId;

    if (this.item != null) {
      let firstProduct = this.item.subProject.project.firstProduct;
      this.processConfigurationInfo(firstProduct);
      this.processTextInfo(firstProduct);
      if (firstProduct.jsonType === ProductTypes.IP_BOARD.value) {
        if (firstProduct.leftRightBoard) {
          this.title = 'Links rechts bord';
        } else if (firstProduct.signId != null) {
          this.title = 'Voorraadbord';
        } else {
          this.title = 'Custom bord';
        }
      } else if (
        [
          ProductTypes.CUSTOMWINDOWSTICKER.value,
          ProductTypes.WINDOWSTICKER.value,
        ].includes(firstProduct.jsonType)
      ) {
        this.title = 'Raamsticker';
      } else if (firstProduct.jsonType === ProductTypes.BOARD.value) {
        this.title = 'Projectbord';
      } else {
        this.title = 'Andere';
      }
      if (this.item.subProject.project.collaborative) {
        this.title = 'Gezamenlijk bord';
      }

      if (this.item.subProject.expectedType === SUB_PROJECT_TYPES.SOLDRENTED) {
        if (firstProduct.type === 'Kleefbord') {
          this.amount = 2;
        } else {
          const baseAmount =
            firstProduct.boardType.ordinal === BoardTypeChoices.V.ordinal ||
            firstProduct.boardType.ordinal ===
              BoardTypeChoices.DOUBLE_SIDED.ordinal
              ? 2
              : 1;
          const secondBoardCopy =
            this.item.subProject.projectPlacement != null &&
            this.item.subProject.projectPlacement.secondBoard != null &&
            this.item.subProject.projectPlacement.secondBoard.secondBoardCopy;
          this.amount = baseAmount * (secondBoardCopy ? 2 : 1);
        }
      }
    }
  }

  private calculateBoardSummary(): void {
    if (
      this.item.subProject.project == null ||
      this.item.subProject.project.firstProduct == null
    ) {
      this.boardSummary = null;
      this.boardSummaryRight = null;
      return;
    }

    const firstProduct = this.item.subProject.project.firstProduct;
    const typeAndFormat = StringHelper.join(
      [firstProduct.type, firstProduct.format],
      ' '
    );
    this.boardSummary = StringHelper.join(
      [
        typeAndFormat,
        firstProduct.stockSignTitle,
        firstProduct.stockSignPhoneNumber,
      ],
      ' / '
    );

    if (firstProduct.leftRightBoard) {
      this.boardSummaryRight = StringHelper.join(
        [
          typeAndFormat,
          firstProduct.stockSignTitleRight,
          firstProduct.stockSignPhoneNumberRight,
        ],
        ' / '
      );
    }
  }

  private calculatePlacementTypePreviewUrl(): void {
    if (
      this.item.subProject.project == null &&
      this.item.subProject.project.firstProduct == null &&
      this.item.subProject.project.firstProduct.boardType == null
    ) {
      this.placementTypePreviewUrl = null;
      return;
    }

    const isAdhesiveBoard =
      this.item.subProject.project.firstProduct.type === 'Kleefbord';
    const mockBoardPart = new IPBoardPart();
    mockBoardPart.boardType =
      this.item.subProject.project.firstProduct.boardType;
    mockBoardPart.placementType =
      this.item.subProject.project.firstProduct.placementType;

    const selectedPlacementOption = isAdhesiveBoard
      ? ClientIPBoardAndPlacementHelper.getInitialSelectedAdhesiveBoardPlacementOption(
          mockBoardPart
        )
      : ClientIPBoardAndPlacementHelper.getInitialSelectedIPBoardPlacementOption(
          mockBoardPart
        );

    this.placementTypePreviewUrl =
      selectedPlacementOption == null
        ? null
        : selectedPlacementOption.previewUrl;
  }

  private processConfigurationInfo(product: AtelierDetailProduct): void {
    const info: AtelierConfigurationInfo =
      AtelierItemDetailHelper.processConfigurationInfo(product);

    for (const key in AtelierConfigurationInfoKeys) {
      this[key] = info[key];
    }
  }

  private processTextInfo(product: AtelierDetailProduct): void {
    if (product.textInfo == null) {
      return;
    }

    this.textCheckboxLabel =
      product.textInfo.textType != null ? product.textInfo.textType : 'Tekst';
    this.textColors = product.textInfo.textColors;

    if (product.textInfoRight == null) {
      return;
    }

    if (
      this.textCheckboxLabel !== 'Tekst' &&
      product.textInfoRight.textType != product.textInfo.textType &&
      product.textInfoRight.textType != null
    ) {
      this.textCheckboxLabel =
        this.textCheckboxLabel.replace('Tekst', 'Tekst (links)') +
        product.textInfoRight.textType.replace('Tekst', ', tekst (rechts)');
    }

    this.textColorsRight = product.textInfoRight.textColors;
  }

  markDoneAtelierInfo(value: boolean): void {
    this.markAsDone(
      value,
      AtelierUpdateCheckedIdentifier.ATELIER_INFO,
      'atelierInfo'
    );
  }

  markDoneAtelierExtraInfo(value: boolean): void {
    this.markAsDone(
      value,
      AtelierUpdateCheckedIdentifier.ATELIER_EXTRA_INFO,
      'atelierExtraInfo'
    );
  }

  markDoneConfiguration(value: boolean): void {
    this.markDoneChecker(
      value,
      AtelierUpdateCheckedIdentifier.CONFIGURATION,
      'configuration'
    );
  }

  markDoneFrame(value: boolean): void {
    this.markDoneChecker(value, AtelierUpdateCheckedIdentifier.FRAME, 'frame');
  }

  markDoneFrameColor(value: boolean): void {
    this.markDoneChecker(
      value,
      AtelierUpdateCheckedIdentifier.FRAME_COLOR,
      'frameColor'
    );
  }

  markDoneTitle(value: boolean): void {
    this.markAsDone(value, AtelierUpdateCheckedIdentifier.TITLE, 'title');
  }

  markDonePhoneNumber(value: boolean): void {
    this.markAsDone(
      value,
      AtelierUpdateCheckedIdentifier.PHONE_NUMBER,
      'phoneNumber'
    );
  }

  markDoneText(value: boolean): void {
    this.markAsDone(value, AtelierUpdateCheckedIdentifier.TEXT, 'text');
  }

  markDoneImmoscoop(value: boolean): void {
    this.markAsDone(
      value,
      AtelierUpdateCheckedIdentifier.IMMOSCOOP,
      'immoscoop'
    );
  }

  markDoneSpotto(value: boolean): void {
    this.markAsDone(value, AtelierUpdateCheckedIdentifier.SPOTTO, 'spotto');
  }

  markDoneExtraBoard(value: boolean): void {
    this.markAsDone(
      value,
      AtelierUpdateCheckedIdentifier.EXTRA_BOARD,
      'extraBoard'
    );
  }

  markDoneSecondBoard(value: boolean): void {
    this.markAsDone(
      value,
      AtelierUpdateCheckedIdentifier.SECOND_BOARD,
      'secondBoard'
    );
  }

  markDoneArrows(value: boolean): void {
    this.markAsDone(value, AtelierUpdateCheckedIdentifier.ARROWS, 'arrows');
  }

  markDoneOtherOrder(value: boolean): void {
    this.markAsDone(
      value,
      AtelierUpdateCheckedIdentifier.OTHER_ORDER,
      'otherOrder'
    );
  }

  markDoneReviewed(value: boolean): void {
    this.markAsDone(value, AtelierUpdateCheckedIdentifier.REVIEWED, 'reviewed');
  }

  markDoneChecker(
    value: boolean,
    type: AtelierUpdateCheckedIdentifier,
    field: string
  ): void {
    this.item.checked[field] = value;
    if (
      this.item.finished &&
      !this.atelierTourItemHelper.isFinished(this.item)
    ) {
      this.modalService.confirmDialog(
        'Atelier klaar',
        'OPGELET! Magazijn stock werd reeds afgetrokken voor deze job. Bent u zeker dat u wil doorgaan met deze actie? Indien ja, gelieve stock aantallen te corrigeren',
        (answer: boolean) => {
          if (answer) {
            this.markAsDone(value, type, field);
          } else {
            this.item.checked[field] = true;
          }
        }
      );
    } else {
      this.markAsDone(value, type, field);
    }
  }

  markAsDone(
    value: boolean,
    type: AtelierUpdateCheckedIdentifier,
    field: string
  ): void {
    this.item.checked[field] = value;
    this.atelierService
      .updateChecked(
        this.item.id,
        type,
        value,
        this.item.subProject.project.firstProduct
          ?.assignedFrameCornerStockAmount > 0
      )
      .then((res) => {
        this.item.finished = this.atelierTourItemHelper.isFinished(this.item);
        this.checkUpdate.emit(res);
        if (
          this.item.finished &&
          this.item.subProject.project.firstProduct != null &&
          this.item.subProject.project.firstProduct.signId != null &&
          !this.item.subProject.project.firstProduct.withoutStock
        ) {
          let amount =
            this.item.subProject.project.firstProduct
              .assignedPhysicalStockAmount;
          let shelfAmount =
            this.item.subProject.project.firstProduct.physicalStockShelfAmount;
          let roofAmount =
            this.item.subProject.project.firstProduct.physicalStockRoofAmount;
          const aMinusB = shelfAmount - amount;
          const output = Math.max(aMinusB, 0);
          const rest = aMinusB < 0 ? Math.abs(aMinusB) : 0;
          shelfAmount = output;
          roofAmount = roofAmount - rest >= 0 ? roofAmount - rest : 0;
          this.stockUpdate.emit({
            signId: this.item.subProject.project.firstProduct.signId,
            shelfStock: shelfAmount,
            roofStock: roofAmount,
          });
        }
      })
      .catch(() => {
        this.item.checked[field] = !value;
        this.snackService.showError(
          'Taak voltooiing kon niet opgeslagen worden.'
        );
      });
  }

  markDoneStockAtBranch(value: boolean): void {
    this.item.checked.stockAtBranch = value;

    this.atelierService
      .updateChecked(
        this.item.id,
        AtelierUpdateCheckedIdentifier.STOCK_AT_BRANCH,
        value
      )
      .then((res) => {
        this.item.finished = this.atelierTourItemHelper.isFinished(this.item);
        this.checkUpdate.emit(res);
      })
      .catch(() => {
        this.item.checked.stockAtBranch = !value;
        this.snackService.showError(
          'Taak voltooiing kon niet opgeslagen worden.'
        );
      });
  }

  openLightbox(
    type: SUB_PROJECT_TYPES,
    leftRight: boolean = false,
    left: boolean = false
  ): void {
    let albums = [];
    let assets = [];
    if (type === SUB_PROJECT_TYPES.BASE_PROJECT) {
      if (leftRight) {
        if (left) {
          assets = [this.item.subProject.leftExample];
        } else {
          assets = [this.item.subProject.rightExample];
        }
      } else {
        assets = this.item.subProject.designPictures;
      }
    } else if (
      type === SUB_PROJECT_TYPES.ADJUSTMENT &&
      !CollectionHelper.isPopulated(this.item.subProject.designPictures)
    ) {
      assets = this.item.subProject.adjustmentAssets;
    } else if (
      type === SUB_PROJECT_TYPES.ADJUSTMENT &&
      CollectionHelper.isPopulated(this.item.subProject.designPictures)
    ) {
      assets = this.item.subProject.designPictures;
    } else if (
      type === SUB_PROJECT_TYPES.SOLDRENTED &&
      !CollectionHelper.isPopulated(this.item.subProject.designPictures) &&
      this.item.subProject.signSticker != null &&
      CollectionHelper.isPopulated(this.item.subProject.signSticker.examples)
    ) {
      assets = this.item.subProject.signSticker.examples.map((a) => a.asset);
    } else if (
      type === SUB_PROJECT_TYPES.SOLDRENTED &&
      CollectionHelper.isPopulated(this.item.subProject.designPictures)
    ) {
      assets = this.item.subProject.designPictures;
    }
    if (CollectionHelper.isPopulated(assets)) {
      for (let asset of assets) {
        const src = '/api/assets/' + asset.uuid;
        const caption = asset.name;
        const thumb = '/api/assets/' + asset.uuid;
        const album = {
          src: src,
          caption: caption,
          thumb: thumb,
        };

        albums.push(album);
      }

      this._lightbox.open(albums, 0);
    }
  }

  openClientUploadedPicturesLightbox(): void {
    if (
      !CollectionHelper.isPopulated(this.item.subProject.clientUploadedPictures)
    ) {
      return;
    }
    const album = AssetHelper.getIAlbums(
      this.item.subProject.clientUploadedPictures
    );
    if (CollectionHelper.isPopulated(album)) {
      this._lightbox.open(album);
    }
  }
}
